import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { colors, customEvent, HLine, Spacing } from "goi_common";

import { isLoggedInState } from "@/atoms/accountState";
import RenewalFooter from "@/containers/HomeContainer/components/RenewalFooter";
import { ParlorDetailProps } from "@/types/parlor";
import ParlorCostIntro from "./components/ParlorCostIntro";
import GuestCountStep, { GUEST_OPTIONS } from "./components/GuestCountStep";
import GuestWhyStep from "./components/GuestWhyStep";
import TimingSheet from "./components/TimingSheet";
import { useE5QuotationSubmit } from "./hooks/useE5QuotationSubmit";
import { armBackTrap } from "./hooks/useParlorRouterChangeLoad";
import { useE5UrgentCall } from "./hooks/useE5UrgentCall";
import { fireE5ReceiveClick, goE5Receive } from "./utils/e5Receive";
import { getCostConfig, getGuestCostRange, GuestRangeKey } from "./utils/parlorIntroCost";

/** E5 가 쓰는 가격 데이터(getStaticProps). 없으면 전국 가운데값으로 계산한다 */
export interface E5Prices {
  foodPerPersonWon: number | null;
  altarWon: number | null;
  altarGradesWon: number[] | null;
}

/**
 * E5 장례식장 명칭 퍼널(B안). 첫 화면(비용 먼저) → 왜 조문객 수 → 조문객 질문 → 비용 구성 → 상황 시트 → 받는 방법.
 * 저장 · 완료 이벤트 · 로그인 이후 흐름은 기존 폼(A)과 같다(useE5QuotationSubmit, e5Receive).
 * 실험 배정은 ParlorRequestContainer 의 E5Gate(검색광고 유입만: 네이버 SA · 구글 광고, Hackle E5_PARLOR_NAME_KEY).
 */
export default function E5Funnel({
  parlor,
  prices: { foodPerPersonWon, altarWon, altarGradesWon },
}: {
  parlor: ParlorDetailProps;
  prices: E5Prices;
}) {
  const onE5Urgent = useE5UrgentCall();
  const isLoggedIn = useRecoilValue(isLoggedInState);
  // E5 화면 단계: 첫 화면 → 왜 조문객 수인지 → 조문객 → 비용 구성 → 시트. 조문객 없음(무빈소)도 비용 구성으로 간다(식사 · 빈소 · 제단꽃이 흐려진 채, 2026-11-05 주완).
  // 위치 확인(「이 위치의 ○○장례식장이 맞나요?」)은 뺐다. 첫 화면이 이미 장례식장 이름을 크게 보여 줘
  // 같은 확인을 한 번 더 묻는 단계였다. 앞에 단계를 더하면 떨어진 기록(#298 · #342 · #345)과 같은 쪽(2026-11-04 주완)
  // 첫 화면과 조문객 질문 사이에 「왜 조문객 수가 필요한지」 한 장(why)을 둔다. 설명 없이 바로 물으면 흐름이 끊겨서(2026-11-05 주완)
  const [e5Stage, setE5Stage] = useState<"intro" | "why" | "q1" | "breakdown">("intro");
  const [e5Guest, setE5Guest] = useState<GuestRangeKey | null>(null);
  // 견적 시기 바텀시트(임박도). 화면을 넘기지 않고 지금 화면 위에 연다
  const [e5SheetOpen, setE5SheetOpen] = useState(false);
  // 시트에서 이미 같은 답(상황 | 조문객)으로 저장했으면(카카오 창에서 돌아온 경우 등) 다시 저장하지 않는다(중복 견적 방지)
  const [e5SavedStatus, setE5SavedStatus] = useState<string | null>(null);
  const { submit: submitE5, loading: e5Submitting } = useE5QuotationSubmit(parlor);
  const e5Range3 = useMemo(
    () => getGuestCostRange(parlor, foodPerPersonWon, altarWon),
    [parlor, foodPerPersonWon, altarWon],
  );
  // 첫 화면 금액: 「100명 내외」 범위의 한가운데, 일의 자리 0(만 원)
  const introAmountManwon = useMemo(() => {
    const r = e5Range3?.byGuest["100명 내외"];
    return r ? Math.round((r.lo + r.hi) / 2 / 10) * 10 : null;
  }, [e5Range3]);
  // ④ 비용 구성에서 고객이 바꾼 빈소 평형. 조문객 수를 바꾸면 그 인원의 추천 빈소로 돌아간다
  const [e5Binso, setE5Binso] = useState<number | null>(null);
  // 제단꽃 등급(0 = 기본). 질문 화면 범위와 같게 기본에서 시작한다
  const [e5Altar, setE5Altar] = useState(0);
  // 식사 인분(없으면 조문객 수만큼). 조문객 수를 바꾸면 그 인원으로 돌아간다
  const [e5Food, setE5Food] = useState<number | null>(null);
  const e5Config = useMemo(
    () =>
      e5Guest
        ? getCostConfig(
            parlor,
            foodPerPersonWon,
            altarGradesWon ?? (altarWon ? [altarWon] : null),
            e5Guest,
            e5Binso,
            e5Altar,
            e5Food,
          )
        : null,
    [parlor, e5Guest, foodPerPersonWon, altarGradesWon, altarWon, e5Binso, e5Altar, e5Food],
  );

  // 첫 화면 노출은 마운트당 한 번. 질문에서 이전으로 돌아와도 다시 쏘지 않도록 첫 화면 안이 아니라 여기서 쏜다
  useEffect(() => {
    customEvent({
      newGtm: { path: "parlor_match_request", trigger_and_target: "e5_view" },
    });
  }, []);

  return (
    <>
      {e5Stage === "intro" ? (
        <ParlorCostIntro
          parlor={parlor}
          amountManwon={introAmountManwon}
          onStart={() => {
            // #370 뒤로가기 이탈 방지를 기존 폼(첫 답에서 무장)과 같게 켠다. 클릭 처리 안에서 동기로 불러야 한다
            armBackTrap();
            setE5Guest(null);
            setE5Stage("why");
            window.scrollTo(0, 0);
          }}
        />
      ) : e5Stage === "why" ? (
        <GuestWhyStep
          onUrgent={onE5Urgent}
          onBack={() => {
            setE5Stage("intro");
            window.scrollTo(0, 0);
          }}
          onNext={() => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_why_next" },
            });
            setE5Stage("q1");
            window.scrollTo(0, 0);
          }}
        />
      ) : (
        // 조문객 질문 → 「왜 이만큼 차이 날까요」 를 같은 화면 안에서 이어 보여 준다(위 비용 카드가 그대로 남는다)
        <GuestCountStep
          range={e5Range3}
          selected={e5Guest}
          onSelect={setE5Guest}
          phase={e5Stage === "breakdown" ? "breakdown" : "question"}
          config={e5Config}
          onUrgent={onE5Urgent}
          onBack={() => {
            if (e5Stage === "breakdown") {
              // 조문객 답은 그대로 둔다(주완: 이전에 고른 게 선택돼 있게)
              setE5Stage("q1");
            } else {
              setE5Stage("why");
            }
            window.scrollTo(0, 0);
          }}
          onNext={(value) => {
            const option = GUEST_OPTIONS.find((o) => o.value === value);
            if (option) {
              customEvent({
                newGtm: { path: "parlor_match_request", trigger_and_target: option.event },
              });
            }
            setE5Binso(null);
            setE5Altar(0);
            setE5Food(null);
            setE5Stage("breakdown");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          // 고객이 직접 자기 상황에 맞추는 행동 = small hook 후보(2026-11-05 주완)
          onGuest={(value) => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_set_guest" },
            });
            setE5Guest(value);
            setE5Binso(null);
            setE5Food(null);
          }}
          onBinso={(pyeong) => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_set_binso" },
            });
            setE5Binso(pyeong);
          }}
          onFood={(servings) => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_set_food" },
            });
            setE5Food(servings);
          }}
          onAltar={(index) => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_set_altar" },
            });
            setE5Altar(index);
          }}
          onExplore={() => {
            customEvent({
              newGtm: { path: "parlor_match_request", trigger_and_target: "e5_explore" },
            });
            setE5SheetOpen(true);
          }}
        />
      )}
      <TimingSheet
        isOpen={e5SheetOpen}
        onClose={() => setE5SheetOpen(false)}
        loading={e5Submitting}
        onSubmit={(status, method) => {
          if (!e5Guest) return;
          fireE5ReceiveClick(method, isLoggedIn);
          // 같은 답(상황 + 조문객)으로 이미 저장했으면 다시 저장하지 않는다. 답이 바뀌었으면 새로 저장해 리드에 맞는 값이 남게
          const savedKey = `${status}|${e5Guest}`;
          if (e5SavedStatus === savedKey) {
            goE5Receive(method);
            return;
          }
          customEvent({
            newGtm: { path: "parlor_match_request", trigger_and_target: "e5_submit" },
          });
          submitE5({
            status,
            condolers: e5Guest,
            onReady: () => {
              setE5SavedStatus(savedKey);
              goE5Receive(method);
            },
          });
        }}
      />
      {/* 첫 화면에만 푸터. 질문 화면에서는 키 큰 화면에서 버튼과 내용 사이에 끼어 보여서 뺀다(주완) */}
      {e5Stage === "intro" && (
        <>
          <Spacing gap={120} />
          <HLine height="1px" color={colors.gray200} />
          <RenewalFooter />
        </>
      )}
    </>
  );
}
